/**
 * MessageList: left rail with 4 groups.
 * 1. «Zuordnung nötig» (!kundeMatched, orange) at the top
 * 2. «Niedrige Konfidenz» (has a task, matched, konfidenz < 90)
 * 3. «Automatisch verarbeitet» (has a task, matched, konfidenz >= 90), collapsed
 * 4. «Keine Aufgabe» (no linked task at all), collapsed. B5: the intake worker stores
 *    internal / Newsletter / noreply mail and skips task creation, so these rows carry
 *    no KI decision at all and never belonged in group 3.
 *
 * Within each group: oldest-first (stable mock order from mock.ts).
 * [Source: docs/design/2026-06-25_sebastian-ux-inventar.md Flow 1]
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, Lock, EyeOff } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { StatusDot } from '../../../components/ui/StatusDot';
import { Badge } from '../../../components/ui/Badge';
import { mailTeamMarkerName } from '../mail-visibility';
import { MailTeamBadge } from './MailTeamBadge';
import type { EingangMail, Triage, Aufgabenstatus } from '../types';
import { KANAL_META, TRIAGE_META, AUFGABENSTATUS_META } from '../mock';
import { KonfidenzBadge } from './konfidenz';
import type { SavedViewKey } from '../mock';
import { EINGANG_VIEWS } from '../mock';

interface MessageListProps {
  messages: EingangMail[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Current saved-view filter */
  viewKey: SavedViewKey;
  onViewChange: (key: SavedViewKey) => void;
  /** All messages (unfiltered) for counting */
  allMessages: EingangMail[];
  search: string;
  onSearchChange: (v: string) => void;
  /** Number after all filters applied */
  filteredCount: number;
}

interface GroupProps {
  label: string;
  items: EingangMail[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  tone?: 'decide' | 'info' | 'ok' | 'neutral';
  defaultCollapsed?: boolean;
}

function MessageGroup({ label, items, selectedId, onSelect, tone = 'neutral', defaultCollapsed = false }: GroupProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  if (items.length === 0) return null;

  return (
    <div>
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="flex w-full items-center gap-1.5 border-b border-border/60 bg-muted/30 px-3 py-1.5 text-left"
      >
        {collapsed ? (
          <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
        )}
        <span
          className={cn(
            'text-xs font-medium',
            tone === 'decide' ? 'text-sem-decide' : 'text-muted-foreground',
          )}
        >
          {label}
        </span>
        <Badge variant={tone === 'decide' ? 'decide' : 'neutral'} className="ml-auto text-[10px]">
          {items.length}
        </Badge>
      </button>
      {!collapsed && (
        <ul>
          {items.map((m) => {
            const active = m.id === selectedId;
            const triage = TRIAGE_META[m.triage as Triage];
            const KanalIcon = KANAL_META[m.kanal].icon;
            // PART A3: inline Aufgabenstatus dot (offen/in Bearbeitung/wartend/erledigt/
            // "keine Aufgabe") when the comm-backed field is present; else the triage dot.
            const status = m.aufgabenstatus
              ? AUFGABENSTATUS_META[m.aufgabenstatus as Aufgabenstatus]
              : { label: triage.label, tone: triage.tone };
            // PART B: reduced tiers render state (never client-side redaction).
            const reduced = m.tier === 'title' || m.tier === 'metadata';
            const restrictedTeam = mailTeamMarkerName(m.restrictedToTeam);
            return (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => onSelect(m.id)}
                  aria-current={active || undefined}
                  className={cn(
                    'flex w-full items-start gap-2.5 border-b border-border/60 p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    active ? 'bg-muted' : 'hover:bg-muted/50',
                  )}
                >
                  <span
                    className={cn(
                      'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md',
                      active ? 'bg-background text-foreground' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    <KanalIcon className="size-3.5" />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-xs font-medium text-foreground">
                        {m.absender}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">{m.zeit}</span>
                    </span>
                    {m.tier === 'metadata' ? (
                      <span className="flex items-center gap-1 truncate text-xs italic text-muted-foreground">
                        <Lock className="size-3 shrink-0" />
                        Inhalt nicht freigegeben
                      </span>
                    ) : (
                      <span className="truncate text-xs text-muted-foreground">
                        {m.betreff || '(kein Betreff)'}
                      </span>
                    )}
                    <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                      <StatusDot tone={status.tone} label={status.label} />
                      {m.isPrivate && (
                        <Badge variant="neutral" className="gap-1 text-[10px]">
                          <EyeOff className="size-2.5" />
                          Privat
                        </Badge>
                      )}
                      {restrictedTeam != null && <MailTeamBadge team={restrictedTeam} />}
                      {reduced && m.tier === 'title' && (
                        <Badge variant="neutral" className="gap-1 text-[10px]">
                          <Lock className="size-2.5" />
                          nur Titel
                        </Badge>
                      )}
                      {!reduced && m.hasTask !== false && <KonfidenzBadge value={m.konfidenz} />}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function MessageList({
  messages,
  selectedId,
  onSelect,
  viewKey,
  onViewChange,
  allMessages,
  search,
  onSearchChange,
  filteredCount,
}: MessageListProps) {
  // 3 groups per Flow 1 spec
  // oldest-first (mock data is already in rough oldest-first within triage order;
  // for safety we do NOT re-sort here — the parent handles filter/sort)
  const zuordnung = messages.filter((m) => !m.kundeMatched);
  const niedrig = messages.filter((m) => m.kundeMatched && m.hasTask !== false && m.konfidenz < 90);
  const automatisch = messages.filter((m) => m.kundeMatched && m.hasTask !== false && m.konfidenz >= 90);
  const ohneAufgabe = messages.filter((m) => m.kundeMatched && m.hasTask === false);

  const isEmpty = messages.length === 0;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Saved-view pill row */}
      <div className="flex shrink-0 flex-wrap gap-1 border-b border-border/60 bg-card px-3 py-2">
        {EINGANG_VIEWS.map((v) => {
          const count = v.triage
            ? allMessages.filter((m) => m.triage === v.triage).length
            : allMessages.length;
          const active = viewKey === v.key;
          return (
            <button
              key={v.key}
              type="button"
              onClick={() => onViewChange(v.key)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                active
                  ? 'bg-foreground text-background'
                  : 'bg-muted text-muted-foreground hover:text-foreground',
              )}
            >
              {v.label}
              {count > 0 && (
                <span
                  className={cn(
                    'tabular-nums',
                    active ? 'text-background/70' : 'text-muted-foreground',
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search / filter bar */}
      <div className="flex shrink-0 items-center gap-2 border-b border-border/60 bg-card px-3 py-2">
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Nachrichten durchsuchen …"
          className="h-8 flex-1 rounded-md border border-border bg-background px-2.5 text-xs text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
          {filteredCount} von {allMessages.length}
        </span>
      </div>

      {/* Message groups */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isEmpty ? (
          <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
            Keine Nachrichten.
          </div>
        ) : (
          <>
            <MessageGroup
              label="Zuordnung nötig"
              items={zuordnung}
              selectedId={selectedId}
              onSelect={onSelect}
              tone="decide"
            />
            <MessageGroup
              label="Niedrige Konfidenz"
              items={niedrig}
              selectedId={selectedId}
              onSelect={onSelect}
              tone="neutral"
            />
            <MessageGroup
              label="Automatisch verarbeitet"
              items={automatisch}
              selectedId={selectedId}
              onSelect={onSelect}
              tone="neutral"
              defaultCollapsed
            />
            <MessageGroup
              label="Keine Aufgabe"
              items={ohneAufgabe}
              selectedId={selectedId}
              onSelect={onSelect}
              tone="neutral"
              defaultCollapsed
            />
          </>
        )}
      </div>
    </div>
  );
}
